'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const categories = [
{
  id: 'engine',
  name: 'Engine Parts',
  description: 'Pistons, liners, gaskets, bearings, camshafts, crankshafts and complete engine overhaul kits for all major truck brands.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb093817-1766472398335.png",
  alt: 'Truck engine components including pistons and gaskets for Mercedes Benz MAN Scania',
  count: '2,400+ SKUs',
  color: 'from-brand-red to-brand-red-dark',
  icon: 'CogIcon',
  featured: true
},
{
  id: 'brakes',
  name: 'Brake Systems',
  description: 'Brake pads, drums, discs, calipers, master cylinders and full air brake system components.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a729ef6d-1767818038798.png",
  alt: 'Heavy truck brake pads and drums for Volvo Scania BPW trailers',
  count: '1,800+ SKUs',
  color: 'from-brand-blue to-brand-blue-dark',
  icon: 'ShieldCheckIcon',
  featured: false
},
{
  id: 'suspension',
  name: 'Suspension & Steering',
  description: 'Leaf springs, shock absorbers, tie rods, ball joints, king pins and complete BPW trailer axle components.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd57ccd5-1771894572494.png",
  alt: 'Truck suspension components leaf springs and shock absorbers for BPW trailers',
  count: '1,200+ SKUs',
  color: 'from-yellow-600 to-yellow-800',
  icon: 'AdjustmentsHorizontalIcon',
  featured: false
},
{
  id: 'filters',
  name: 'Filters & Fluids',
  description: 'Oil filters, air filters, fuel filters, cabin filters and hydraulic filters for all commercial vehicle brands.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7e964e7-1771076403790.png",
  alt: 'Engine filters oil air and fuel filters for trucks and European cars',
  count: '900+ SKUs',
  color: 'from-green-700 to-green-900',
  icon: 'FunnelIcon',
  featured: false
},
{
  id: 'electrical',
  name: 'Electrical & Lighting',
  description: 'Alternators, starters, ECUs, sensors, wiring harnesses and full LED lighting systems for trucks and trailers.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13999ece2-1768364558067.png",
  alt: 'Truck electrical components alternators starters and LED lighting systems',
  count: '750+ SKUs',
  color: 'from-brand-red to-orange-700',
  icon: 'BoltIcon',
  featured: false
},
{
  id: 'chassis',
  name: 'Chassis & Bodywork',
  description: 'Chassis cross members, fifth wheel couplings, landing legs, mudguards and full trailer body components.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18701a23f-1766582220487.png",
  alt: 'Truck chassis components fifth wheel coupling and trailer landing legs',
  count: '600+ SKUs',
  color: 'from-brand-blue-dark to-slate-900',
  icon: 'WrenchScrewdriverIcon',
  featured: false
}];


export default function PartsCategories() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <section id="categories" className="py-24 bg-brand-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden" data-reveal>
          <p className="text-brand-blue text-xs font-heading font-black uppercase tracking-widest mb-3">
            Parts Catalogue
          </p>
          <h2 className="font-heading font-black text-brand-dark text-3xl md:text-5xl tracking-tight mb-4">
            Comprehensive Parts{' '}
            <span className="text-gradient-red-blue">Categories</span>
          </h2>
          <p className="text-brand-muted font-body max-w-2xl mx-auto text-base md:text-lg">
            Over 7,000 SKUs across 6 major categories. From engine internals to trailer chassis — we have what your fleet needs.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {categories.map((cat, index) =>
          <div
            key={cat.id}
            className={`category-card reveal-hidden-scale stagger-${Math.min(index + 1, 6)}`}
            data-reveal
            onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
            style={{ height: index === 0 ? '320px' : '260px' }}>
            
              <AppImage
              src={cat.image}
              alt={cat.alt}
              fill
              className="object-cover" />
            
              {/* Gradient overlay always present */}
              <div
              className={`absolute inset-0 bg-gradient-to-t ${cat.color} opacity-70`} />
            
              {/* Hover overlay */}
              <div className="category-overlay">
                <div className="text-center text-white px-6">
                  <p className="font-heading font-black text-xl mb-2">{cat.name}</p>
                  <p className="font-body text-sm opacity-90 leading-relaxed">{cat.description}</p>
                  <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="text-white font-heading font-bold text-xs">{cat.count}</span>
                  </div>
                </div>
              </div>
              {/* Always-visible label */}
              <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name={cat.icon as 'CogIcon'} size={16} className="text-white/80" />
                      <span className="text-white/70 text-xs font-heading font-bold uppercase tracking-widest">
                        {cat.count}
                      </span>
                    </div>
                    <h3 className="font-heading font-black text-white text-lg md:text-xl leading-tight">
                      {cat.name}
                    </h3>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <Icon name="ArrowRightIcon" size={14} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Inquiry CTA */}
        <div className="mt-12 text-center reveal-hidden" data-reveal>
          <p className="text-brand-muted font-body mb-4">
            Can&apos;t find a specific part? Our team sources hard-to-find components across East Africa.
          </p>
          <a
            href="https://wa.me/256772500220?text=Hello%20Baninvest%2C%20I%20need%20a%20specific%20spare%20part%20for%20my%20truck."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex">
            
            <Icon name="ChatBubbleLeftRightIcon" size={18} className="text-white" />
            Request a Specific Part
          </a>
        </div>
      </div>
    </section>);

}